import React, { useState, useEffect } from 'react';

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Layout from '../../containers/Layout';
import LoansTable from '../../components/LoansTable';
import BorrowerLoansTable from '../../components/BorrowerLoansTable';
import AddOrEditPageTabs from '../../components/AddOrEditPage/AddOrEditPageTabs';

import LoanService from '../../services/LoanService';
import { useServicerStore } from '../../store';
import useQueryParam from '../../hooks/useQueryParam';

const tabs = [{ label: 'Active' }, { label: 'Archive' }, { label: 'Review' }];

export default function LoansPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTotal, setPageTotal] = useState(1);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const adminTabs = [
    {
      label: 'Loans',
      onClick: () => {},
      active: true,
    },
    {
      label: 'Imports',
      onClick: () => {
        navigate('/loans/imports');
      },
    },
  ];

  const userRole = useServicerStore((state) => state.userRole);
  const userId = useServicerStore((state) => state.userId);
  const isSuperAdmin = userRole === 'superAdmin';
  const isBorrower = userRole === 'borrower';
  const isLender = userRole === 'lender';

  const { getQueryParam } = useQueryParam('tab');
  const tab = getQueryParam() || 'Active';

  const { data: loans, isFetching } = useQuery({
    queryKey: ['loans', currentPage, userId, userRole, tab],
    queryFn: () => {
      const params = {
        page: currentPage,
        limit: 25,
      };

      if (userRole === 'test') {
        params.lenderIds = [userId];
      }

      let status = ['active'];

      if (tab === 'Archive') {
        status = ['paid', 'archived'];
      } else if (tab === 'Review') {
        status = ['pending', 'review'];
      }

      if (isLender) {
        params.status = status;
      }

      return LoanService.getLoans(params);
    },
    placeholderData: keepPreviousData,
  });

  // If user is a lender, redirect to the first tab
  useEffect(() => {
    if (isLender && !getQueryParam()) {
      navigate('/loans?tab=Active', { replace: true });
    }
  }, [getQueryParam, isLender, navigate]);

  useEffect(() => {
    if (loans) {
      setPageTotal(loans.pageTotal);
    }
  }, [loans]);

  const switchToPage = (selected) => {
    const page = selected.selected + 1;
    if (page < 1 || page > pageTotal) {
      return;
    }

    setCurrentPage(page);
  };

  return (
    <Layout>
      <div className="flex">
        {/* Content area */}
        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <main className="grow">
            <div className="px-4 sm:px-6 lg:px-8 py-8 pb-10 w-full max-w-9xl mx-auto">
              {/* Page header */}
              <div className="sm:flex sm:justify-between sm:items-center mb-8">
                {/* Left: Title */}
                <div className="mb-4 sm:mb-0">
                  <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                    {t('LoansPage.Loans')}
                  </h1>
                </div>

                {/* Right: Actions */}
                {isSuperAdmin && (
                  <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
                    {/* Add loan button */}
                    <Link
                      to="/loans/new?tab=Loan"
                      className="btn bg-indigo-500 hover:bg-indigo-600 text-white"
                    >
                      <svg
                        className="w-4 h-4 fill-current opacity-50 shrink-0"
                        viewBox="0 0 16 16"
                      >
                        <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                      </svg>
                      <span className="hidden xs:block ml-2">Add Loan</span>
                    </Link>
                  </div>
                )}
              </div>
              {isLender && (
                <div className="mb-2 sm:w-60 relative z-0">
                  <AddOrEditPageTabs tabs={tabs} />
                </div>
              )}
              {isSuperAdmin && (
                <div className="mb-2 sm:w-60 relative z-0">
                  <AddOrEditPageTabs tabs={adminTabs} />
                </div>
              )}

              {isBorrower ? (
                <BorrowerLoansTable rows={loans} />
              ) : (
                <LoansTable
                  total={loans?.itemsTotal}
                  rows={loans?.items}
                  isFetching={isFetching}
                  pageTotal={pageTotal}
                  switchToPage={switchToPage}
                  currentPage={currentPage}
                  isLenderSection={isLender}
                  isLoansPage
                />
              )}
            </div>
          </main>
        </div>
      </div>
    </Layout>
  );
}
